<template>
    <div class="my-6">
        <h3 class="font-bold">List of {{ route.name }}</h3>
        <span class="text-sm text-gray-500">From this screen, you could enable or disable the weeks in which you want your
            users to be able to enter time records. Remembering that this control does not overlap the project date.
        </span>
    </div>
    <div class="my-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Select Year</label>
        <select v-model="selectedYear" @change="loadData" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
        </select>
    </div>
    <div class="card">
        <DataTable :headers="headers" :data="data" :options="options" color="primary" />
    </div>
</template>
  
<script>
import DataTable from "../../components/Elements/Datatable.vue";

import { inject, onMounted, ref } from "vue";
import { GET, PATCH, POST } from "../../services/api";
import { useRoute, useRouter } from "vue-router";

export default {
    name: "controlers",
    components: {
        DataTable
    },
    setup() {
        const headers = [
            { key: "Month", value: "month" },
            { key: "Week", value: "weekNumber" },
            { key: "Date Range", value: "dateRange" },
            { key: "Status", value: "active" },
            { key: "Options", value: "" },
        ];

        const route = useRoute();
        const router = useRouter();
        const modal = inject("modal");
        const alert = inject("alert");
        const loader = inject("loading");
        const data = ref([]);
        const selectedYear = ref(new Date().getFullYear());
        const availableYears = ref([]);

        const getWeeksOfYear = (year) => {
            const weeks = [];
            const firstDayOfYear = new Date(year, 0, 1);
            const lastDayOfYear = new Date(year, 11, 31);

            const currentWeekStart = new Date(firstDayOfYear);
            const dayOfWeek = currentWeekStart.getDay();
            const diff = currentWeekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            currentWeekStart.setDate(diff);

            let weekNumber = 1;
            const monthNames = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

            while (currentWeekStart <= lastDayOfYear) {
                const currentWeekEnd = new Date(currentWeekStart);
                currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

                if (currentWeekEnd.getFullYear() === year) {
                    const month = monthNames[currentWeekStart.getMonth()];
                    const startDate = currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const endDate = currentWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                    weeks.push({
                        id: weekNumber,
                        weekNumber: weekNumber,
                        dateRange: `${startDate} - ${endDate}`,
                        month: month,
                        startDate: currentWeekStart.toISOString().split('T')[0],
                        endDate: currentWeekEnd.toISOString().split('T')[0],
                        status: true,
                        active: 'active',
                        type: 'week'
                    });
                }

                currentWeekStart.setDate(currentWeekStart.getDate() + 7);
                weekNumber++;
            }

            return weeks;
        };

        const getMonthsOfYear = (year) => {
            const months = [];
            const monthNames = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

            for (let i = 0; i < 12; i++) {
                months.push({
                    id: i + 1,
                    name: monthNames[i],
                    month: monthNames[i],
                    month_value: i,
                    status: true,
                    active: 'active',
                    year: year
                });
            }

            return months;
        };

        const loadData = async () => {
            try {
                loader.open();
                const year = selectedYear.value;
                const existingMonths = await GET("controls/months");
                const existingWeeks = await GET("controls/weeks");

                const monthsOfYear = getMonthsOfYear(year);
                const weeksOfYear = getWeeksOfYear(year);

                for (const month of monthsOfYear) {
                    const existingControl = existingMonths.find(el => el.month_value === month.month_value && el.year === year);
                    if (!existingControl) {
                        await POST("controls/month", {
                            name: month.name,
                            month_value: month.month_value,
                            year: year,
                            status: true
                        });
                    }
                }

                for (const week of weeksOfYear) {
                    const existingControl = existingWeeks.find(el => el.weekNumber === week.weekNumber && el.year === year);
                    if (!existingControl) {
                        await POST("controls/week", {
                            weekNumber: week.weekNumber,
                            year: year,
                            startDate: week.startDate,
                            endDate: week.endDate,
                            month: week.month,
                            status: true
                        });
                    }
                }

                const updatedMonths = await GET("controls/months");
                const updatedWeeks = await GET("controls/weeks");

                const monthData = monthsOfYear.map(month => {
                    const existingControl = updatedMonths.find(el => el.month_value === month.month_value && el.year === year);
                    return {
                        ...month,
                        id: existingControl ? existingControl.id : month.id,
                        status: existingControl ? existingControl.status : true,
                        active: (existingControl ? existingControl.status : true) == true ? 'active' : 'desactive',
                        type: 'month'
                    };
                });

                const weekData = weeksOfYear.map(week => {
                    const existingControl = updatedWeeks.find(el => el.weekNumber === week.weekNumber && el.year === year);
                    return {
                        ...week,
                        id: existingControl ? existingControl.id : week.id,
                        status: existingControl ? existingControl.status : true,
                        active: (existingControl ? existingControl.status : true) == true ? 'active' : 'desactive',
                        type: 'week',
                    };
                });

                const monthNames = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"];

                const sortedData = [];
                for (let i = 0; i < 12; i++) {
                    const monthName = monthNames[i];
                    const monthItem = monthData.find(m => m.month === monthName);
                    if (monthItem) {
                        sortedData.push(monthItem);
                    }
                    const monthWeeks = weekData.filter(w => w.month === monthName);
                    sortedData.push(...monthWeeks);
                }

                data.value = sortedData;

                loader.close();
            } catch (e) {
                loader.close();
                if (e.includes("Token") || e.includes("t_dynamik") || e == "Unauthorized") {
                    localStorage.removeItem("t_dynamik");
                    localStorage.removeItem("info_dynamik");
                    router.push({ path: '/login' });
                }
                alert.open("Error", e, "danger");
            }
        };

        onMounted(() => {
            const currentYear = new Date().getFullYear();
            availableYears.value = [currentYear - 4,currentYear - 3,currentYear - 2,currentYear - 1, currentYear, currentYear + 1];
            loadData();
        });


        const changeStatus = (item) => {
            try {

                const statusFunction = async function () {
                    try {
                        loader.open();
                        const endpoint = item.type === 'month' ? `controls/month/${item.id}` : `controls/week/${item.id}`;
                        await PATCH(endpoint, { status: !item.status });

                        await loadData();

                        loader.close();
                        const itemType = item.type === 'month' ? 'Month' : 'Week';
                        const itemName = item.type === 'month' ? item.name : item.weekNumber;
                        alert.open("Success!", `${itemType} <b>${itemName}</b> was updated successfully!`, "success");
                    } catch (e) {
                        loader.close();
                        if (e.includes("Token") || e.includes("t_dynamik") || e == "Unauthorized") {
                            localStorage.removeItem("t_dynamik");
                            localStorage.removeItem("info_dynamik");
                            router.push({ path: '/login' });
                        }
                        alert.open("Error", e, "danger");
                    }
                }

                loader.open();
                const itemType = item.type === 'month' ? 'month' : 'week';
                modal.open(
                    "Attention",
                    `Do you really wish to change this ${itemType} status?`,
                    "warning",
                    "Yes",
                    () => statusFunction(),
                    modal.close()
                );
                loader.close();
            } catch (error) {
                loader.close();
            }
        }

        // const show = (r) => {
        //   router.push(`/parceiros/editar/${r.id}`);
        // };

        const options = [
        { icon: "block", action: (param) => changeStatus(param), title: "Change status", use:'status' },
        ];

        return { headers, data, options, route, selectedYear, availableYears, loadData };
    },
};
</script>
  
<style></style>